import { useMemo, useEffect } from 'react';
import {
  queryClient as globalQueryClient,
  emitQueuedSubmissionFailure,
} from 'common/infrastructure/remoteState/persistQueryClient';
import {
  useGetAllPhotosByAsset,
  createPhoto,
  linkPhotosToAsset,
  getAllPhotosByAsset,
  getGetAllPhotosByAssetQueryKey,
} from 'api-client';
import { Tp2Id, Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';
import TenantRepository from 'common/infrastructure/tenant/repository';
// Asset-specific helpers
import { cachedPhotoToCustomFile } from '../asset/assetPhotoHelpers';

// Generic infrastructure
import {
  getPhotoCache,
  updatePhotoCache,
  removePhotoFromCache,
  registerPendingPhotoLink,
  getEntityTp2Id,
  clearEntityTp2Id,
  clearPendingPhotoLinks,
} from 'common/infrastructure/photo-management/upload.cache';
import { CustomFile } from 'common/infrastructure/photo-management/types';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import {
  MutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';

function assetPhotosQueryKey(
  tenantId: Tp3Id,
  assetId: Tp3Id,
  assetClassId: Tp3Id,
  tp2AssetId?: Tp2Id
) {
  const params: { assetId: Tp3Id; assetClassId: Tp3Id; tp2AssetId?: Tp2Id } = {
    assetId,
    assetClassId,
  };
  if (tp2AssetId !== undefined) {
    params.tp2AssetId = tp2AssetId;
  }
  return getGetAllPhotosByAssetQueryKey(tenantId, params);
}

async function getAttachedPhotoIds(
  tenantId: Tp3Id,
  assetId: Tp3Id,
  assetClassId: Tp3Id,
  tp2AssetId: Tp2Id
): Promise<Set<Tp3Id>> {
  const attached = await getAllPhotosByAsset(tenantId, { assetId, assetClassId, tp2AssetId });
  return new Set((attached ?? []).map((photo) => photo.id));
}

async function discardCachedPhotoIfUploaded(entityId: Tp3Id, photoId: Tp3Id): Promise<void> {
  const cached = await getPhotoCache(entityId);
  const photo = cached.find((entry) => entry.id === photoId);
  if (!photo) return;

  if (photo.uploadStatus === 'success') {
    await removePhotoFromCache(entityId, photoId);
    return;
  }

  await updatePhotoCache(entityId, photoId, { uploadStatus: 'error', lastAttempt: Date.now() });
}

/**
 * TP2 appends photos without de-duplicating, and any retry — a mutation-level retry, or a
 * resume after a restart — may be re-running over a link that already landed, so read back
 * what is attached and send only the remainder.
 */
async function linkPhotosGuarded(
  tenantId: Tp3Id,
  assetId: Tp3Id,
  assetClassId: Tp3Id,
  tp2AssetId: Tp2Id,
  photoIds: Tp3Id[]
): Promise<void> {
  const attachedIds = await getAttachedPhotoIds(tenantId, assetId, assetClassId, tp2AssetId);
  const remainingIds = photoIds.filter((id) => !attachedIds.has(id));
  if (remainingIds.length === 0) return;

  await linkPhotosToAsset(tenantId, assetId, remainingIds, { assetClassId, tp2AssetId });
}

export interface LinkPhotosToAssetVars {
  assetId: Tp3Id;
  assetClassId: Tp3Id;
  tp2AssetId: Tp2Id;
  photoIds: Tp3Id[];
  tenantId: Tp3Id;
}

export interface UploadPhotoToAssetVars {
  photoId: Tp3Id;
  base64Data: string;
  entityId: Tp3Id;
  assetClassId: Tp3Id;
  tp2AssetId?: Tp2Id;
  fileName: string;
  tenantId: Tp3Id;
}

/**
 * Repository for handling asset photos - both from server and offline storage
 */

interface ServerPhoto {
  name: string;
  id: string;
  dateUploaded: string;
  uploadStatus?: string;
  base64Data?: string;
}

const PhotoRepository = {
  /**
   * Fetches photos for an asset from both server and offline storage
   */
  useAssetPhotos(assetClassId: string, assetId: string, tp2AssetId?: number) {
    const tenantId = TenantRepository.getCurrentTenantPid();

    // Build params object, only including tp2AssetId if it exists
    const params: {
      assetId: string;
      assetClassId: string;
      tp2AssetId?: number;
    } = {
      assetId,
      assetClassId,
    };

    if (tp2AssetId !== undefined) {
      params.tp2AssetId = tp2AssetId;
    }

    const useQueryResponse = useGetAllPhotosByAsset(tenantId, params);

    // Keep track of offline photos
    const [offlinePhotos, setOfflinePhotos] = useMemo(() => {
      const offlinePhotoState: CustomFile[] = [];
      return [
        offlinePhotoState,
        (photos: CustomFile[]) => {
          offlinePhotoState.length = 0;
          offlinePhotoState.push(...photos);
        },
      ];
    }, []);

    // Load offline photos
    useEffect(() => {
      const loadOfflinePhotos = async () => {
        loggerService.log(LoggerLevel.DEBUG, 'Loading offline photos for asset', {
          assetId,
          assetClassId,
        });

        const cachedPhotos = await getPhotoCache(assetId);
        if (cachedPhotos.length > 0) {
          loggerService.log(LoggerLevel.DEBUG, 'Found offline photos', {
            assetId,
            photoCount: cachedPhotos.length,
            photos: cachedPhotos.map((p) => ({ id: p.id, name: p.name, status: p.uploadStatus })),
          });

          const customFiles = cachedPhotos.map(cachedPhotoToCustomFile);
          setOfflinePhotos(customFiles);
        }
      };

      loadOfflinePhotos();
    }, [assetId, assetClassId, setOfflinePhotos]);

    // Handle successful data fetch to cache for offline use
    useEffect(() => {
      const handleSuccessfulFetch = async () => {
        if (useQueryResponse?.data) {
          await PhotoRepository.prefetchOfflinePhotos(assetClassId, assetId, tp2AssetId);
        }
      };

      handleSuccessfulFetch();
    }, [useQueryResponse?.data, assetClassId, assetId, tp2AssetId]);

    // Combine server and offline photos
    const photos = useMemo(() => {
      const serverPhotos = useQueryResponse?.data || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const uniquePhotos = new Map<string, any>();

      // Add server photos first
      serverPhotos.forEach((photo) => {
        uniquePhotos.set(photo.name, photo);
      });

      // Then add offline photos, potentially overriding server ones
      // if they have the same name but are newer versions
      offlinePhotos.forEach((photo) => {
        const photoDate = (photo as CustomFile & { dateUploaded?: string }).dateUploaded;
        const existingPhoto = uniquePhotos.get(photo.name);
        const existingPhotoDate = (existingPhoto as ServerPhoto).dateUploaded;

        if (!existingPhoto || (photoDate && existingPhotoDate && photoDate > existingPhotoDate)) {
          uniquePhotos.set(photo.name, photo);
        }
      });

      return Array.from(uniquePhotos.values());
    }, [useQueryResponse?.data, offlinePhotos]);

    return {
      ...useQueryResponse,
      photos,
      isLoading: useQueryResponse.isPending,
      isPending: useQueryResponse.isPending,
      error: useQueryResponse.error,
    };
  },

  /**
   * Pre-loads offline photos and merges them with the query cache
   */
  async prefetchOfflinePhotos(assetClassId: string, assetId: string, tp2AssetId?: number) {
    const queryClient = globalQueryClient;
    try {
      const offlinePhotos = await getPhotoCache(assetId);

      if (offlinePhotos.length > 0) {
        // Build query key, only including tp2AssetId if it exists
        const queryKey =
          tp2AssetId !== undefined
            ? ['getAllPhotosByAsset', assetId, assetClassId, tp2AssetId]
            : ['getAllPhotosByAsset', assetId, assetClassId];
        const currentData = queryClient.getQueryData<ServerPhoto[]>(queryKey) || [];

        const existingPhotos = new Map();
        if (currentData && Array.isArray(currentData)) {
          currentData.forEach((photo) => {
            existingPhotos.set(photo.name, photo);
          });
        }

        const newPhotos = offlinePhotos.filter((photo) => !existingPhotos.has(photo.name));

        if (newPhotos.length > 0) {
          const combinedData = [
            ...currentData,
            ...newPhotos.map((photo) => ({
              name: photo.name,
              id: photo.id,
              uploadStatus: photo.uploadStatus,
              base64Data: photo.base64Data,
            })),
          ];

          queryClient.setQueryData(queryKey, combinedData);
        }
      }

      return offlinePhotos;
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, 'Failed to prefetch offline photos', {
        assetId,
        assetClassId,
        error: error instanceof Error ? error.message : String(error),
      });
      return [];
    }
  },

  /**
   * Non-hook function for linking pending photos
   * when tp2AssetId is available
   * (can be used in callbacks)
   */
  getUploadPhotoToAssetMutationConfig(): MutationOptions {
    return {
      mutationKey: ['uploadPhotoToAsset'],
      // Inherits the shared retry predicate deliberately: `retry: 3` used to override it,
      // so a blocked link exhausted after 4 attempts and the photo was lost. Retrying a
      // connectivity failure forever is safe here because createPhoto is skipped once the
      // cache says it landed and the link reads back what is already attached.
      ...defaultMutationOptions,
      retryDelay: (attempt: number) => Math.min(500 * 2 ** attempt, 4000),
      mutationFn: async ({
        photoId,
        base64Data,
        entityId,
        assetClassId,
        tp2AssetId,
        fileName,
        tenantId,
      }: UploadPhotoToAssetVars) => {
        const cached = await getPhotoCache(entityId);
        const alreadyUploaded = cached.some(
          (photo) => photo.id === photoId && photo.uploadStatus === 'success'
        );

        if (!alreadyUploaded) {
          await createPhoto(tenantId, photoId, { filename: fileName, data: base64Data, tenantId });
          await updatePhotoCache(entityId, photoId, { uploadStatus: 'success', base64Data: '' });
        }

        if (tp2AssetId) {
          await linkPhotosGuarded(tenantId, entityId, assetClassId, tp2AssetId, [photoId]);
        }
      },
      onMutate: () => undefined,
      onSuccess: async (
        _: unknown,
        { photoId, entityId, assetClassId, tp2AssetId, tenantId }: UploadPhotoToAssetVars
      ) => {
        if (tp2AssetId) {
          await removePhotoFromCache(entityId, photoId);
          await globalQueryClient.invalidateQueries({
            queryKey: assetPhotosQueryKey(tenantId, entityId, assetClassId, tp2AssetId),
          });
          sessionStorage.setItem(`photos-refetch-needed-${entityId}`, 'true');
        } else {
          // No tp2AssetId yet (new asset) — photo uploaded but not linked.
          // Mark as pending so linkPendingPhotosDirectly can link it once the asset is created.
          await updatePhotoCache(entityId, photoId, {
            uploadStatus: 'success',
            linkStatus: 'pending',
          });
          await registerPendingPhotoLink(entityId, photoId, { assetClassId });

          const createdTp2AssetId = await getEntityTp2Id(entityId);
          if (createdTp2AssetId !== undefined) {
            await PhotoRepository.linkPendingPhotosDirectly(
              entityId,
              assetClassId,
              createdTp2AssetId
            ).catch(() => undefined);
          }
        }
      },
      onError: async (err: unknown, { photoId, entityId }: UploadPhotoToAssetVars) => {
        loggerService.log(LoggerLevel.ERROR, 'Photo upload to asset failed', err);
        await discardCachedPhotoIfUploaded(entityId, photoId);
      },
      onSettled: () => undefined,
    };
  },

  /**
   * Returns shared mutation callbacks for unlinking photos from an asset.
   * Used both in mutation defaults (for persisted/resumed mutations on page reload)
   * and in the direct call site (ManagePhotosTab) so the cache is updated immediately
   * on success regardless of which path triggered the mutation.
   */
  getUnlinkPhotosFromAssetMutationCallbacks() {
    return {
      onSuccess: (
        _data: unknown,
        variables: {
          tenantId: string;
          assetId: string;
          data: string[];
          params: { assetClassId: string; tp2AssetId?: number };
        }
      ) => {
        // Build params the same way useAssetPhotos does — omit tp2AssetId when undefined
        // so the key matches exactly what was stored in the cache.
        const params: { assetClassId: string; assetId: string; tp2AssetId?: number } = {
          assetClassId: variables.params.assetClassId,
          assetId: variables.assetId,
        };
        if (variables.params.tp2AssetId !== undefined) {
          params.tp2AssetId = variables.params.tp2AssetId;
        }
        const queryKey = getGetAllPhotosByAssetQueryKey(variables.tenantId, params);
        globalQueryClient.refetchQueries({ queryKey, type: 'all' });
        sessionStorage.setItem(`photos-refetch-needed-${variables.assetId}`, 'true');
      },
      onError: (
        error: unknown,
        variables: {
          tenantId: string;
          assetId: string;
          data: string[];
          params: { assetClassId: string; tp2AssetId?: number };
        }
      ) => {
        loggerService.log(LoggerLevel.ERROR, 'Unlink photos from asset error', {
          error: error instanceof Error ? error.message : String(error),
          tenantId: variables.tenantId,
          assetId: variables.assetId,
        });
      },
      onSettled: (
        _data: unknown,
        _error: unknown,
        variables?: {
          tenantId: string;
          assetId: string;
          data: string[];
          params: { assetClassId: string; tp2AssetId?: number };
        }
      ) => {
        if (!variables) return;
        const params: { assetClassId: string; assetId: string; tp2AssetId?: number } = {
          assetClassId: variables.params.assetClassId,
          assetId: variables.assetId,
        };
        if (variables.params.tp2AssetId !== undefined) {
          params.tp2AssetId = variables.params.tp2AssetId;
        }
        globalQueryClient.invalidateQueries({
          queryKey: getGetAllPhotosByAssetQueryKey(variables.tenantId, params),
        });
      },
    };
  },

  /**
   * Offline-capable link mutation. Registered in the mutation registry so it behaves like
   * every other write in the app: it parks while offline, is persisted with its variables,
   * and is resumed by resumePausedMutations on reconnect and on app start. This used to be
   * a bare API call, which is why a failed link was unrecoverable — nothing enumerated the
   * pending links to retry them.
   */
  getLinkPhotosToAssetMutationConfig(): MutationOptions {
    return {
      mutationKey: ['linkPhotosToAsset'],
      ...defaultMutationOptions,
      mutationFn: async ({
        assetId,
        assetClassId,
        tp2AssetId,
        photoIds,
        tenantId,
      }: LinkPhotosToAssetVars) =>
        linkPhotosGuarded(tenantId, assetId, assetClassId, tp2AssetId, photoIds),
      onMutate: () => undefined,
      onSuccess: async (
        _: unknown,
        { assetId, assetClassId, tp2AssetId, photoIds, tenantId }: LinkPhotosToAssetVars
      ) => {
        for (const photoId of photoIds) {
          await removePhotoFromCache(assetId, photoId);
        }
        await clearPendingPhotoLinks(assetId);
        await clearEntityTp2Id(assetId);

        await globalQueryClient.invalidateQueries({
          queryKey: assetPhotosQueryKey(tenantId, assetId, assetClassId, tp2AssetId),
        });
        sessionStorage.setItem(`photos-refetch-needed-${assetId}`, 'true');

        loggerService.log(LoggerLevel.DEBUG, 'Linked photos to asset', {
          assetId,
          tp2AssetId,
          linkedCount: photoIds.length,
        });
      },
      onError: async (error: unknown, { assetId, tp2AssetId, photoIds }: LinkPhotosToAssetVars) => {
        const message = error instanceof Error ? error.message : String(error);
        loggerService.log(LoggerLevel.ERROR, 'Failed to link photos to asset', {
          assetId,
          tp2AssetId,
          message,
        });

        // The bytes are already on the server, so the cached copy is only dead weight now.
        for (const photoId of photoIds) {
          await removePhotoFromCache(assetId, photoId);
        }
        await clearPendingPhotoLinks(assetId);
        await clearEntityTp2Id(assetId);

        emitQueuedSubmissionFailure({ kind: 'photoLink', message, reference: tp2AssetId });
      },
      onSettled: () => undefined,
    };
  },

  /**
   * Dispatches the persisted link mutation for every photo still pending on an asset.
   * Called from the two points that can first know both halves are ready: createAsset's
   * success (asset id resolved) and the upload's success (photo uploaded).
   */
  async linkPendingPhotosDirectly(
    assetId: Tp3Id,
    assetClassId: Tp3Id,
    tp2AssetId: Tp2Id
  ): Promise<{ success: boolean; linkedCount: number }> {
    const tenantId = TenantRepository.getCurrentTenantPid();
    const cachedPhotos = await getPhotoCache(assetId);
    const photoIds = cachedPhotos
      .filter((photo) => photo.id && photo.linkStatus === 'pending')
      .map((photo) => photo.id)
      .filter((id): id is string => Boolean(id));

    if (photoIds.length === 0) {
      loggerService.log(LoggerLevel.DEBUG, 'No pending photos to link', { assetId });
      return { success: true, linkedCount: 0 };
    }

    for (const photoId of photoIds) {
      await updatePhotoCache(assetId, photoId, { linkStatus: 'linking' });
    }

    const variables: LinkPhotosToAssetVars = {
      assetId,
      assetClassId,
      tp2AssetId,
      photoIds,
      tenantId,
    };

    // Built through the MutationCache because this runs inside another mutation's callback,
    // where no hook is available and v5 no longer exposes queryClient.executeMutation.
    const mutation = globalQueryClient
      .getMutationCache()
      .build(globalQueryClient, { mutationKey: ['linkPhotosToAsset'] });

    await mutation.execute(variables);
    return { success: true, linkedCount: photoIds.length };
  },
};

export default PhotoRepository;
